import { Link, Outlet, useLocation } from 'react-router'
import { useDocumentTitle } from '../lib/crumbs'
import { SgdsMasthead } from '@govtechsg/sgds-web-component/react'
import { AgencyMark } from './AgencyMark'
import { GovFooter } from './GovFooter'

/**
 * The chrome the statutory notices render in.
 *
 * ## Why the notices are not inside `Shell`
 *
 * SGDS requires the Privacy and Terms links on every page, and a link that refuses the
 * reader is not a link — so the notices have to be readable by someone who has not signed
 * in. `Shell` sits behind `RequireAuth`, so a notice inside it would redirect a visitor to
 * the sign-in screen, which is the one place a privacy statement is most likely to be
 * wanted.
 *
 * They therefore sit outside `RequireAuth`, in this chrome: the mandated government
 * masthead, the same brand band the shell and the sign-in screen carry, and the footer.
 *
 * ## The way back works signed in and signed out, without asking which
 *
 * The wordmark is a link to `/`. Signed in, `RequireAuth` lets that through and the index
 * route sends the officer to their dashboard; signed out, it sends them to sign-in. One
 * link, correct in both states, and no auth branching in a component that has no business
 * knowing about auth.
 */
const NOTICE_TITLES: Readonly<Record<string, string>> = {
  '/privacy': 'Privacy statement',
  '/terms': 'Terms of use',
  '/contact-us': 'Contact us',
}

export function NoticeChrome() {
  // ★ F-22: the tab names the notice, not just the product.
  const { pathname } = useLocation()
  useDocumentTitle(NOTICE_TITLES[pathname] ?? 'Notice')
  return (
    <div className="min-h-screen flex flex-col">
      <a href="#pt-main" className="pt-skip-link">
        Skip to main content
      </a>

      {/* The mandated bar, ahead of branding — SGDS is explicit about the order. */}
      <SgdsMasthead fluid={false} />

      <header className="pt-brandband">
        <AgencyMark />
        <Link to="/" className="pt-wordmark" style={{ color: 'inherit', textDecoration: 'none' }}>
          PRS2.0
        </Link>
        <span className="pt-divider" aria-hidden="true" />
        <span className="text-sm opacity-90">Public Service Division</span>
      </header>

      <div className="flex-1 px-4 sm:px-6 py-6 max-w-[1440px] w-full mx-auto">
        <main id="pt-main">
          <Outlet />
        </main>
      </div>

      <GovFooter />
    </div>
  )
}
